import { ContentCopySharp } from '@mui/icons-material';
import { Alert, Box, Button, Grid, IconButton, InputAdornment, Modal, Paper, Snackbar, TextField, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { RegeneratePassword } from '../../apiRequest/user';
import { t } from '../../features/i18n';

let color: string = '#f18733';

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    borderTop: `8px solid ${color}`,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: '40px',
  },
  tableRow: {
    '&:not(:last-child)': {
      borderBottom: 'none',
    },
  },
}));

interface IResetPasswordModal {
  open: boolean;
  onClose: () => void;
  userCode: string | undefined;
}

const ResetPasswordModal: React.FC<IResetPasswordModal> = ({ open, onClose, userCode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [passwordCopiedAlert, setPasswordCopiedAlert] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (open) {
      setLoading(false);
      setSuccessAlertOpen(false);
      setNewPassword('');
    }
  }, [open]);

  const handleCancel = () => {
    onClose();
  };

  const handleResetPassword = async () => {
    if (!userCode) {
      console.error('User code is required for password reset.');
      return;
    }

    try {
      setLoading(true);
      const response = await RegeneratePassword(userCode);
      setNewPassword(response.password);
      setSuccessAlertOpen(true);
    } catch (error) {
      console.error('Error while resetting password:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Paper className={classes.paper}>
          <Box>
            <Box
              sx={{
                maxHeight: '50px',
                textAlign: 'left',
                color: color,
              }}
            >
              <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                <Grid item>
                  <Typography
                    sx={{
                      fontSize: '24px',
                    }}
                  >
                    {t('regenerate_password_title')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ mt: 2, mb: 3 }}>
              <Typography sx={{ mb: 1 }}>{t('regenerate_password_confirmation')}</Typography>
              <Alert sx={{ mb: 2 }} severity="warning">
                {t('regenerate_password_warning')}
              </Alert>

              <Button
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading}
                sx={{
                  width: 140,
                  height: 40,
                  bgcolor: color,
                  '&:hover': {
                    backgroundColor: color,
                  },
                  fontWeight: 'bold',
                }}
              >
                {loading ? t('processing') : t('regenerate_password')}
              </Button>

              {successAlertOpen && (
                <TextField
                  value={newPassword}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  disabled
                  label={t('new_password')}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            try {
                              navigator.clipboard.writeText(newPassword);
                              setPasswordCopiedAlert(true);
                            } catch (error) {
                              console.error('Error copying password to clipboard:', error);
							  alert(t('copy_password_error'));
                            }
                          }}
                          edge="end"
                        >
                          <ContentCopySharp />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ my: 2 }}
                />
              )}
            </Box>

            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                width: 140,
                height: 40,
                color: color,
                textTransform: 'none',
                fontWeight: 'bold',
                borderColor: color,
              }}
            >
              {t('ok')}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Password copied snackbar */}
      <Snackbar
        open={passwordCopiedAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2000}
        onClose={() => setPasswordCopiedAlert(false)}
        message={t('password_copied')}
      />
    </>
  );
};

export default ResetPasswordModal;
